import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  FormControl
} from '@mui/material';
import { ContentCopy, PersonAdd, Delete, Save } from '@mui/icons-material';
import api from '../../services/authService';
import { useAuth } from '../../contexts/AuthContext';

const OrganizationManagement = () => {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [inviteCode, setInviteCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [hfToken, setHfToken] = useState('');
  const [savingApiKeys, setSavingApiKeys] = useState(false);

  useEffect(() => {
    fetchMembers();
    fetchInviteCode();
    fetchApiKeys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMembers = async () => {
    try {
      const response = await api.get('/organizations/current/members');
      setMembers(response.data);
    } catch (err) {
      setError('Failed to load organization members');
    } finally {
      setLoading(false);
    }
  };

  const fetchInviteCode = async () => {
    try {
      const response = await api.get('/organizations/current/invite-code');
      setInviteCode(response.data.inviteCode);
    } catch (err) {
      console.error('Failed to load invite code');
    }
  };

  const fetchApiKeys = async () => {
    try {
      const response = await api.get('/organizations/current/api-keys');
      setApiKey(response.data.api_key || '');
      setHfToken(response.data.hf_token || '');
    } catch (err) {
      console.error('Failed to load API keys');
    }
  };

  const handleSaveApiKeys = async () => {
    try {
      setSavingApiKeys(true);
      await api.put('/organizations/current/api-keys', {
        api_key: apiKey,
        hf_token: hfToken
      });
      setSuccess('API keys updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to update API keys');
    } finally {
      setSavingApiKeys(false);
    }
  };

  const handleRegenerateCode = async () => {
    try {
      const response = await api.post('/organizations/current/regenerate-invite-code');
      setInviteCode(response.data.inviteCode);
      setSuccess('Invite code regenerated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to regenerate invite code');
    }
  };

  const handleCopyInviteCode = () => {
    navigator.clipboard.writeText(inviteCode);
    setSuccess('Invite code copied to clipboard!');
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleCopyInviteLink = () => {
    const link = `${window.location.origin}/join-org?code=${inviteCode}`;
    navigator.clipboard.writeText(link);
    setSuccess('Invite link copied to clipboard!');
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await api.put(`/organizations/current/members/${userId}/role`, {
        orgRole: newRole
      });
      setSuccess('Member role updated successfully');
      setTimeout(() => setSuccess(''), 3000);
      fetchMembers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update member role');
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this member?')) {
      return;
    }

    try {
      await api.delete(`/organizations/current/members/${userId}`);
      setSuccess('Member removed successfully');
      setTimeout(() => setSuccess(''), 3000);
      fetchMembers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove member');
    }
  };

  if (loading) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4">
            Organization Management
          </Typography>
          <Button
            variant="contained"
            startIcon={<PersonAdd />}
            onClick={() => setShowInviteDialog(true)}
          >
            Invite Members
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* API Configuration Section */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            API Configuration
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Configure API keys for your organization. These keys will be used for all transcription processing within your organization.
          </Typography>

          <TextField
            fullWidth
            label="OpenAI API Key"
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder="sk-..."
            helperText="Your OpenAI API key is required for transcription processing"
            margin="normal"
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="HuggingFace Token"
            type="password"
            value={hfToken}
            onChange={(e) => setHfToken(e.target.value)}
            placeholder="hf_..."
            helperText="Your HuggingFace token for model access"
            margin="normal"
            sx={{ mb: 2 }}
          />

          <Button
            variant="contained"
            color="primary"
            startIcon={savingApiKeys ? <CircularProgress size={20} color="inherit" /> : <Save />}
            onClick={handleSaveApiKeys}
            disabled={savingApiKeys}
          >
            {savingApiKeys ? 'Saving...' : 'Save API Keys'}
          </Button>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Team Members ({members.length})
          </Typography>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Joined</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {members.map((member) => (
                  <TableRow key={member.id}>
                    <TableCell>
                      {member.firstName} {member.lastName}
                    </TableCell>
                    <TableCell>{member.email}</TableCell>
                    <TableCell>
                      {user?.orgRole === 'org_admin' && member.id !== user.id ? (
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <Select
                            value={member.orgRole}
                            onChange={(e) => handleUpdateRole(member.id, e.target.value)}
                          >
                            <MenuItem value="member">Member</MenuItem>
                            <MenuItem value="org_admin">Org Admin</MenuItem>
                          </Select>
                        </FormControl>
                      ) : (
                        <Chip
                          label={member.orgRole === 'org_admin' ? 'Org Admin' : 'Member'}
                          color={member.orgRole === 'org_admin' ? 'primary' : 'default'}
                          size="small"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={member.accountStatus}
                        color={member.accountStatus === 'active' ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(member.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="right">
                      {user?.orgRole === 'org_admin' && member.id !== user.id && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveMember(member.id)}
                        >
                          <Delete />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Invite Dialog */}
        <Dialog open={showInviteDialog} onClose={() => setShowInviteDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Invite Team Members</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="textSecondary" paragraph>
              Share this invite code or link with your team members:
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="caption" color="textSecondary">
                Invite Code:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
                <TextField
                  value={inviteCode}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
                <IconButton onClick={handleCopyInviteCode}>
                  <ContentCopy />
                </IconButton>
              </Box>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="textSecondary">
                Invite Link:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
                <TextField
                  value={`${window.location.origin}/join-org?code=${inviteCode}`}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
                <IconButton onClick={handleCopyInviteLink}>
                  <ContentCopy />
                </IconButton>
              </Box>
            </Box>

            <Alert severity="info" sx={{ mt: 2 }}>
              Team members must have an active subscription or trial to join your organization.
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleRegenerateCode} color="warning">
              Regenerate Code
            </Button>
            <Button onClick={() => setShowInviteDialog(false)} variant="contained">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default OrganizationManagement;
